import { retryableLoad } from "@mirage/service-conversation/service/conversationsSync/retryable";
import { tagged } from "@mirage/service-logging";
import { Observable, Subject, type Subscription } from "rxjs";
import { v4 as uuidv4 } from "uuid";

import type {
  ConversationMessagesServerMetadata,
  ConversationsDataSyncInterface,
  ConversationsMetadataSyncInterfaceInternal,
  DataAPI,
  DataStorage,
  StoredConversation,
} from "@mirage/service-conversation/service/conversationsSync/types";
import type {
  Conversation,
  ConversationClientContext,
  ConversationMessage,
} from "@mirage/service-conversation/types";

const logger = tagged("conversationsSync/dataSync");

// sync logic for all conversations data (e.g. messages and drafts)
export function createConversationsDataSync(
  storage: DataStorage,
  api: DataAPI,
  metadataSync: ConversationsMetadataSyncInterfaceInternal,
): ConversationsDataSyncInterface {
  const conversationDataSyncs = new Map<
    string,
    ReturnType<typeof createConversationDataSync>
  >();

  // get a conversation data sync for a given conversation ID or create a new one if it doesn't exist
  const getConversationDataSync = (
    conversationID: string,
    knownServerConversationID: string | undefined,
  ): ReturnType<typeof createConversationDataSync> => {
    let conversationDataSync = conversationDataSyncs.get(conversationID);
    if (!conversationDataSync) {
      conversationDataSync = createConversationDataSync(
        storage,
        api,
        metadataSync,
        conversationID,
        knownServerConversationID,
      );
      conversationDataSyncs.set(conversationID, conversationDataSync);
    }
    return conversationDataSync;
  };

  const getConversationDataSyncByServerId = async (
    serverConversationID: string,
  ) => {
    // check if there is a running conversation with the given server conversation ID
    const allDataSyncConversations = await Promise.all(
      Array.from(conversationDataSyncs.entries()).map(
        async ([conversationID, conversationDataSync]) => {
          const conversation = await conversationDataSync.get();
          return {
            id: conversationID,
            conversation,
            conversationDataSync,
          };
        },
      ),
    );
    const foundConversation = allDataSyncConversations.find(
      ({ conversation }) =>
        conversation.serverConversationId === serverConversationID,
    );
    if (foundConversation) {
      logger.info(
        `found in-memory conversation with server conversation ID: ${serverConversationID}`,
      );
      return {
        dataSync: foundConversation.conversationDataSync,
        id: foundConversation.id,
      };
    }

    // if not, check if there is a conversation in storage with the given server conversation ID
    const storedConversations = await storage.loadConversations();
    for (const [conversationId, conversation] of Object.entries(
      storedConversations,
    )) {
      if (conversation.serverConversationId === serverConversationID) {
        logger.info(
          `found stored conversation with server conversation ID: ${serverConversationID}`,
        );
        return {
          dataSync: getConversationDataSync(
            conversationId,
            serverConversationID,
          ),
          id: conversationId,
        };
      }
    }

    // create a new conversation data sync for the given server conversation ID if we have never
    // seen it before
    const newConversationID = uuidv4();
    logger.info(
      `creating new conversation for server conversation ID: ${serverConversationID}`,
    );
    return {
      dataSync: getConversationDataSync(
        newConversationID,
        serverConversationID,
      ),
      id: newConversationID,
    };
  };

  const deleteConversations: ConversationsDataSyncInterface["deleteConversations"] =
    async (conversationIDs) => {
      for (const conversationID of conversationIDs) {
        const conversationDataSync = conversationDataSyncs.get(conversationID);
        if (conversationDataSync) {
          conversationDataSyncs.delete(conversationID);
        }
        // TODO: deal with in-flight update calls (worst case it may come back after deletion)
      }

      storage.deleteConversations(conversationIDs);
      metadataSync.deleteCachedConversationMetadatas(conversationIDs);

      // call APIs to delete conversations
      await api.deleteConversations(conversationIDs);
    };

  return {
    getConversation: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.get();
    },
    getConversationByServerId: async (serverConversationID) => {
      const { dataSync, id } =
        await getConversationDataSyncByServerId(serverConversationID);
      const conversation = await dataSync.get();
      return {
        ...conversation,
        id,
      };
    },
    refreshConversation: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.refresh();
    },
    createNewConversation: async () => {
      const newConversationID = uuidv4();
      const conversationDataSync = getConversationDataSync(
        newConversationID,
        undefined,
      );
      const conversation = await conversationDataSync.get();
      return {
        ...conversation,
        id: newConversationID,
      };
    },
    updateConversationMessages: (
      conversationID,
      messages,
      response,
      clientContext,
    ) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        response?.serverConversationId,
      );
      return conversationDataSync.updateMessages(
        messages,
        response,
        clientContext,
      );
    },
    updateConversationDraft: (conversationID, draft) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.updateDraft(draft);
    },
    getConversationObservable: (conversationID) => {
      const conversationDataSync = getConversationDataSync(
        conversationID,
        undefined,
      );
      return conversationDataSync.getObservable();
    },
    deleteConversations,
  };
}

// sync logic for a single conversation
function createConversationDataSync(
  storage: DataStorage,
  api: DataAPI,
  metadataSync: ConversationsMetadataSyncInterfaceInternal,
  conversationID: string,
  knownServerConversationID: string | undefined,
) {
  /* cached conversation, mapped by local conversation ID */
  let cachedConversation: Promise<Conversation> | undefined = undefined;
  /* subject that emits latest conversation */
  const conversationSubject = new Subject<Conversation>();
  /* set if there is an ongoing refresh that needs to be interrupted if a new refresh is requested */
  let retryOngoingRefresh: (() => void) | undefined = undefined;
  /* set if there is an ongoing draft update that needs to be interrupted if a new draft update is requested */
  let retryOngoingDraftUpdate: (() => void) | undefined = undefined;

  const loadFromAPI = async (
    serverConversationID: string,
    prev?: Conversation | undefined,
  ): Promise<Conversation> => {
    const apiConversation = await api.getConversation(serverConversationID);

    let conversation = apiConversation.conversation;
    if (prev && prev.messages.length === conversation.messages.length) {
      // augment API conversation properties with previous conversation. these are properties that
      // are not returned by the API but are returned by message responses.
      conversation = {
        ...conversation,
        suggestedQueries: prev.suggestedQueries,
        progressUpdates: prev.progressUpdates,
      };
    }

    // save the conversation to storage + update metadata
    storage.saveConversation(conversationID, conversation);
    // update (or insert) cached metadata with the latest metadata from the server
    metadataSync.updateCachedConversationMetadata(
      serverConversationID,
      apiConversation.metadata,
    );

    // emit and return
    conversationSubject.next(conversation);
    return conversation;
  };

  const firstLoad = async (): Promise<StoredConversation> => {
    const storedConversations = await storage.loadConversations();
    const storedConversation: StoredConversation | undefined =
      storedConversations[conversationID];

    const serverConversationID =
      knownServerConversationID || storedConversation?.serverConversationId;
    if (serverConversationID) {
      // if local conversation is dirty, start by saving it to the server
      if (storedConversation?.isDirty) {
        if (storedConversation.markdownDraft) {
          await api.updateConversationDraft(
            serverConversationID,
            storedConversation.markdownDraft,
          );
        } else {
          logger.error(
            `found dirty conversation but no draft to save: ${conversationID}`,
          );
        }
      }

      // load the conversation from the server
      return await loadFromAPI(serverConversationID, storedConversation);
    } else {
      // no known server conversation ID, no-op as this is a new conversation
    }

    // emit and return
    const conversation = storedConversation || {
      messages: [],
      suggestedQueries: [],
      markdownDraft: undefined,
      serverConversationId: undefined,
    };
    conversationSubject.next(conversation);
    return conversation;
  };

  const get = (): Promise<StoredConversation> => {
    if (!cachedConversation) {
      cachedConversation = firstLoad();
    }
    return cachedConversation;
  };

  const refresh = async (): Promise<Conversation> => {
    const existingConversation = await get();
    const serverConversationID = existingConversation.serverConversationId;
    if (!serverConversationID) {
      // no server conversation ID, no-op as this is a new conversation
      return existingConversation;
    }

    // using retryableLoad here so an update in the middle of a refresh will discard the ongoing
    // refresh to avoid races where newly created conversations will be deleted by an ongoing
    // refresh.
    const { promise, retry } = retryableLoad(async () => {
      const existingConversation = await get();
      return loadFromAPI(serverConversationID, existingConversation);
    });
    retryOngoingRefresh = retry;

    // wait for the promise to resolve
    const conversation = await promise;
    retryOngoingRefresh = undefined;

    // update cached conversation + return
    cachedConversation = promise;
    return conversation;
  };

  const updateMessages = async (
    messages: ConversationMessage[],
    response?: ConversationMessagesServerMetadata,
    clientContext?: ConversationClientContext,
  ): Promise<Conversation> => {
    const conversation = await get();
    const updatedConversation: Conversation = {
      ...conversation,
      messages: messages,
    };
    if (response) {
      // newly received response from server, update aux metadata from response
      updatedConversation.serverConversationId = response.serverConversationId;
      updatedConversation.suggestedQueries = response.suggestedQueries;
      updatedConversation.markdownDraft = response.markdownDraft;
      updatedConversation.progressUpdates = response.progressUpdates;
      updatedConversation.assistRequestId = response.assistRequestId;
    }

    if (clientContext) {
      // update client context if present
      updatedConversation.clientContext = clientContext;
    }

    // update cached conversation + emit
    cachedConversation = Promise.resolve(updatedConversation);
    conversationSubject.next(updatedConversation);

    // update storage + interrupt any ongoing refresh to avoid races where refresh
    // wipes out an update
    storage.saveConversation(conversationID, updatedConversation);
    retryOngoingRefresh?.();

    return updatedConversation;
  };

  const updateDraft = async (draft: string): Promise<Conversation> => {
    const conversation = await get();
    const updatedConversation = {
      ...conversation,
      markdownDraft: draft,
      isDirty: true, // mark as dirty to persist to server on next open if page closed before saving
    };

    // update cached conversation + emit
    cachedConversation = Promise.resolve(updatedConversation);
    conversationSubject.next(updatedConversation);

    // update storage + interrupt any ongoing refresh to avoid races where refresh
    // wipes out an update
    storage.saveConversation(conversationID, updatedConversation);
    retryOngoingRefresh?.();

    // queue an API update but don't wait for it to complete
    if (conversation.serverConversationId) {
      queueDraftUpdate();
    } else {
      // no server conversation ID, no-op as this is a new conversation
    }

    return updatedConversation;
  };

  const queueDraftUpdate = async (): Promise<void> => {
    if (retryOngoingDraftUpdate) {
      // if there is an ongoing draft update, tell it to pick up new changes and retry
      retryOngoingDraftUpdate();
    } else {
      const { promise, retry } = retryableLoad(runDraftUpdate);
      retryOngoingDraftUpdate = retry;
      // mark draft as saved after the update completes
      promise
        .then(markDraftAsSaved)
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          retryOngoingDraftUpdate = undefined;
        });
    }
  };

  const runDraftUpdate = async (): Promise<void> => {
    // pick up the latest draft and update the server if needed
    const conversation = await get();
    if (
      conversation.serverConversationId &&
      conversation.isDirty &&
      conversation.markdownDraft
    ) {
      await api.updateConversationDraft(
        conversation.serverConversationId,
        conversation.markdownDraft,
      );
    }
  };

  const markDraftAsSaved = async (): Promise<void> => {
    // mark as draft as having been saved and no longer dirty
    const conversation = await get();
    const updatedConversation = {
      ...conversation,
      isDirty: false,
    };
    cachedConversation = Promise.resolve(updatedConversation);
    conversationSubject.next(updatedConversation);
    storage.saveConversation(conversationID, updatedConversation);
  };

  const getObservable = (): Observable<Conversation> => {
    return new Observable((subscriber) => {
      let subscription: Subscription | undefined;

      // When subscribing, start by sending them the initial state
      get()
        .then((conversation) => {
          subscriber.next(conversation);
          subscription = conversationSubject.subscribe(subscriber);
          return subscription;
        })
        .catch((error) => {
          return subscriber.error(error);
        });

      return () => {
        subscription?.unsubscribe();
      };
    });
  };

  return {
    get,
    refresh,
    updateMessages,
    updateDraft,
    getObservable,
  };
}
